import { FileUploadFileMimeType } from '@chakra-ui/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { CoreType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';

export type args = {
  postAttachmentsPresignedUrls: ApiType<CoreType.PostAttachmentsPresignedUrls>['args'];
  getPartsListByProjectAndRelease: ApiType<CoreType.GetPartsListByProjectAndRelease>['args'];
  getAttachmentFileType: { url: string };
};
export type responses = {
  postAttachmentsPresignedUrls: ApiType<CoreType.PostAttachmentsPresignedUrls>['response'];
  getPartsListByProjectAndRelease: ApiType<CoreType.GetPartsListByProjectAndRelease>['response'];
  getAttachmentFileType: FileUploadFileMimeType;
};

export const coreApi = createApi({
  reducerPath: 'coreApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Core'],
  endpoints: (builder) => ({
    postAttachmentsPresignedUrls: builder.mutation<
      responses['postAttachmentsPresignedUrls'],
      args['postAttachmentsPresignedUrls']
    >({
      query: ({ body }) => ({
        url: '/core/attachments/presign/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    getPartsListByProjectAndRelease: builder.query<
      responses['getPartsListByProjectAndRelease'],
      args['getPartsListByProjectAndRelease']
    >({
      query: ({ query }) => ({
        url: '/core/parts/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Core',
                id: `parts-${_arg?.query?.project ?? 'all'}-${_arg?.query?.release ?? 'all'}`,
              },
            ]
          : [],
    }),

    getAttachmentFileType: builder.query<string | undefined, { url: string }>({
      async queryFn({ url }) {
        try {
          const res = await fetch(url, { method: 'GET' });

          // Get MIME type
          const contentType = res.headers.get('content-type') ?? undefined;

          // Abort reading body to save memory
          res.body?.cancel();

          return { data: contentType };
        } catch (err: any) {
          return { error: { status: 'FETCH_ERROR', error: err.message } };
        }
      },
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Core', id: _arg.url }] : [],
    }),
  }),
});

export const {
  usePostAttachmentsPresignedUrlsMutation,
  useGetPartsListByProjectAndReleaseQuery,
  useGetAttachmentFileTypeQuery,
} = coreApi;

export namespace CoreApi {
  export const Api = coreApi;
  export type Args = args;
  export type Responses = responses;
}
